/**
 * Client-side image validation for the wizard, matching what the backend upload
 * endpoints accept (`image/jpeg`, `image/png`, `image/webp`, ≤5 MB). Catches
 * unsupported files (e.g. AVIF/GIF/SVG) and oversized files at selection time so
 * a seller never gets an "Unsupported file type" / "File too large" failure after
 * finishing setup.
 */
export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/** OS file-picker filter (a hint only — selection is still validated below). */
export const IMAGE_ACCEPT = ALLOWED_IMAGE_TYPES.join(",");

export type ImageErrorKind = "type" | "size";

export function validateWizardImage(file: File): ImageErrorKind | null {
  if (
    !ALLOWED_IMAGE_TYPES.includes(
      file.type as (typeof ALLOWED_IMAGE_TYPES)[number],
    )
  )
    return "type";
  if (file.size > MAX_IMAGE_BYTES) return "size";
  return null;
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error("read-failed"));
    r.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("decode-failed"));
    img.src = src;
  });
}

/**
 * Downscale + re-encode an image to a data URL whose longest edge is at most
 * `maxEdge`px. A logo never needs to be larger, and this keeps the uploaded
 * bytes tiny — so the upload can't hit the backend's "File too large" limit
 * regardless of how it's configured. PNG output preserves logo transparency.
 * Falls back to the original (validated, ≤5 MB) data URL if canvas isn't usable.
 */
export async function downscaleImageToDataUrl(
  file: File,
  maxEdge = 512,
): Promise<string> {
  const original = await readAsDataUrl(file);
  try {
    const img = await loadImage(original);
    const longest = Math.max(img.width, img.height) || 1;
    const scale = Math.min(1, maxEdge / longest);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return original;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/png");
  } catch {
    return original;
  }
}
